import {
    AlipayOutlined,
    CrownOutlined,
    DollarOutlined,
    ReloadOutlined,
    UserOutlined
} from '@ant-design/icons';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    InputNumber,
    Modal,
    Row,
    Space,
    Spin,
    Statistic,
    Tag,
    Typography,
    message
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import alipayService from '../services/alipay';
import memberService from '../services/member';
import './MemberInfo.less';

const { Title, Text, Paragraph } = Typography;

const MemberInfo = ({ visible, onClose }) => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [memberInfo, setMemberInfo] = useState(null);
  const [memberLevels, setMemberLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wechatModalVisible, setWechatModalVisible] = useState(false);
  
  // 支付宝充值相关状态
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(9.9);
  const [rechargePreview, setRechargePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // 用户自充值功能已移除，现在由管理员进行人工充值

  // 加载会员信息
  useEffect(() => {
    if (visible && isAuthenticated) {
      loadMemberInfo();
    }
  }, [visible, isAuthenticated]);

  // 监听会员信息更新事件
  useEffect(() => {
    const handleMemberInfoUpdated = (event) => {
      setMemberInfo(event.detail.memberInfo);
    };

    window.addEventListener('member-info-updated', handleMemberInfoUpdated);
    return () => {
      window.removeEventListener('member-info-updated', handleMemberInfoUpdated);
    };
  }, []);

  const loadMemberInfo = async () => {
    setLoading(true);
    try {
      const info = await memberService.getMemberInfo();
      setMemberInfo(info);
      
      // 尝试加载会员等级列表，失败时使用默认值
      try {
        const levels = await memberService.getMemberLevels();
        setMemberLevels(levels.levels || []);
      } catch (levelError) {
        console.warn('加载会员等级列表失败，使用默认值:', levelError);
        setMemberLevels([]);
      }
    } catch (error) {
      console.error('加载会员信息失败:', error);
      message.error('加载会员信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadMemberInfo();
    await refreshUser(); // 同时刷新用户信息
    message.success('会员信息已刷新');
  };

  // 显示微信二维码
  const showWechatQR = () => {
    setWechatModalVisible(true);
  };

  // 关闭微信二维码弹窗
  const handleWechatModalClose = () => {
    setWechatModalVisible(false);
  };

  // 支付宝充值相关函数
  const showRechargeModal = () => {
    setRechargeModalVisible(true);
    // 首次打开时加载预览
    handlePreviewRecharge(rechargeAmount);
  };

  const handleRechargeModalClose = () => {
    setRechargeModalVisible(false);
    setRechargePreview(null);
  };

  // 预览充值信息
  const handlePreviewRecharge = async (amount) => {
    if (!amount || amount < 9.9) {
      message.warning('充值金额不能少于 9.9 元');
      return;
    }

    setLoadingPreview(true);
    try {
      const preview = await alipayService.previewRecharge(amount);
      setRechargePreview(preview);
    } catch (error) {
      console.error('预览充值失败:', error);
      message.error(error.response?.data?.detail || '预览充值失败');
    } finally {
      setLoadingPreview(false);
    }
  };

  // 充值金额变化时自动预览
  const handleAmountChange = (value) => {
    setRechargeAmount(value);
    if (value && value >= 9.9) {
      handlePreviewRecharge(value);
    }
  };

  // 提交支付
  const handleSubmitPayment = async () => {
    if (!rechargeAmount || rechargeAmount < 9.9) {
      message.warning('充值金额不能少于 9.9 元');
      return;
    }

    setProcessingPayment(true);
    try {
      // 创建订单并打开支付页面
      const order = await alipayService.createOrder(rechargeAmount, 'page');
      
      message.info('支付页面已打开，请在新窗口完成支付');
      
      // 打开支付页面
      alipayService.openPayment(order.payment_url);
      
      // 开始轮询订单状态
      alipayService.pollOrderStatus(
        order.out_trade_no,
        async (orderStatus) => {
          // 支付成功
          message.success(`充值成功！获得 ${orderStatus.points_earned} 积分`);
          setProcessingPayment(false);
          setRechargeModalVisible(false);
          
          // 刷新会员信息和用户信息
          await loadMemberInfo();
          await refreshUser();
        },
        (error) => {
          // 支付失败或超时
          setProcessingPayment(false);
          if (error.error === '查询超时') {
            message.warning('支付状态查询超时，请稍后在订单列表中查看');
          } else if (error.status === 'failed') {
            message.error('支付失败');
          } else if (error.status === 'cancelled') {
            message.info('支付已取消');
          }
        }
      );
      
    } catch (error) {
      console.error('创建支付订单失败:', error);
      message.error(error.response?.data?.detail || '创建支付订单失败');
      setProcessingPayment(false);
    }
  };

  // 用户自充值功能已移除



  const getMemberStatusTag = () => {
    if (!memberInfo) return null;

    const levelColorMap = {
      'normal': 'default',
      'white': 'processing',
      'silver': 'success',
      'gold': 'warning',
      'diamond': 'cyan',
      'supreme': 'magenta'
    };

    const levelIconMap = {
      'normal': <UserOutlined />,
      'white': <CrownOutlined />,
      'silver': <CrownOutlined />,
      'gold': <CrownOutlined />,
      'diamond': <CrownOutlined />,
      'supreme': <CrownOutlined />
    };

    // 确保有默认值
    const memberLevel = memberInfo.member_level || 'normal';
    const memberLevelName = memberInfo.member_level_name || (memberInfo.is_member ? '会员' : '普通用户');
    
    const color = levelColorMap[memberLevel] || 'default';
    const icon = levelIconMap[memberLevel] || <UserOutlined />;

    if (memberInfo.is_member) {
      return (
        <Badge.Ribbon text={memberLevelName} color={color}>
          <Tag 
            icon={icon} 
            color={color} 
            style={{ 
              fontSize: '14px', 
              padding: '4px 12px',
              fontWeight: 'bold'
            }}
          >
            {memberLevelName}
          </Tag>
        </Badge.Ribbon>
      );
    } else {
      return (
        <Tag 
          icon={icon} 
          color={color} 
          style={{ 
            fontSize: '14px', 
            padding: '4px 12px'
          }}
        >
          {memberLevelName}
        </Tag>
      );
    }
  };



  if (!isAuthenticated) {
    return (
      <Modal
        title="会员信息"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={500}
      >
        <Alert
          message="需要登录"
          description="请先登录以查看会员信息"
          type="warning"
          showIcon
        />
      </Modal>
    );
  }

  return (
    <>
      <Modal
        title={
          <Space>
            <CrownOutlined />
            会员信息
          </Space>
        }
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="refresh" onClick={handleRefresh} loading={loading}>
            <ReloadOutlined />
            刷新
          </Button>,
          <Button key="close" onClick={onClose}>
            关闭
          </Button>
        ]}
        width={900}
      >
        {memberInfo ? (
          <div className="member-info-content">
            {/* 用户信息总览 */}
            <Card 
              size="small" 
              style={{ 
                marginBottom: 16,
                background: 'linear-gradient(135deg, #f6f8fa 0%, #ffffff 100%)',
                border: '1px solid #e8e8e8'
              }}
              title={
                <Space>
                  <UserOutlined style={{ color: '#1890ff' }} />
                  <span>用户信息</span>
                </Space>
              }
            >
              <Row gutter={[24, 24]}>
                {/* 左侧：个人基本信息 */}
                <Col xs={24} md={14}>
                  <div style={{ padding: '8px 0' }}>
                    <Descriptions column={2} size="small" labelStyle={{ fontWeight: '500', color: '#333' }}>
                      <Descriptions.Item label="姓名">
                        <Text style={{ fontSize: '14px', fontWeight: '500' }}>
                          {user?.full_name || user?.username || '未设置'}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="手机号码">
                        <Text copyable={!!user?.phone}>
                          {user?.phone || '未设置'}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="邮箱地址">
                        <Text copyable={!!user?.email}>
                          {user?.email || '未设置'}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="所属机构" span={2}>
                        <Text>{user?.organization || '未设置'}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="用户ID" span={2}>
                        <Text copyable style={{ fontSize: '12px', color: '#666' }}>
                          {memberInfo.user_id}
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                </Col>
                
                {/* 右侧：积分信息 */}
                <Col xs={24} md={10}>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    background: '#f0f9ff',
                    borderRadius: '12px',
                    border: '1px solid #bae7ff'
                  }}>
                    <Statistic
                      title="积分余额"
                      value={memberInfo.bean_coins}
                      prefix={<DollarOutlined />}
                      suffix="分"
                      valueStyle={{ color: '#faad14', fontSize: '24px', fontWeight: 'bold' }}
                      style={{ textAlign: 'center' }}
                    />
                    <div style={{ 
                      marginTop: '12px',
                      fontSize: '12px',
                      color: '#666',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      {memberInfo.is_member 
                        ? `${memberInfo.member_level_name || '会员'}，单次最多处理 2000 张化验单`
                        : `${memberInfo.member_level_name || '普通用户'}，单次最多处理 20 张化验单`
                      }
                    </div>
                    
                    {/* 总计充值金额 */}
                    <div style={{ 
                      marginTop: '16px',
                      padding: '12px',
                      background: '#fff7e6',
                      borderRadius: '8px',
                      border: '1px solid #ffd591',
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#666', 
                        marginBottom: '4px' 
                      }}>
                        累计充值
                      </div>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#fa8c16' 
                      }}>
                        ¥{memberInfo.total_recharge_amount?.toFixed(2) || '0.00'}
                      </div>
                      {memberInfo.current_discount_rate && !isNaN(memberInfo.current_discount_rate) && memberInfo.current_discount_rate < 1.0 && (
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#52c41a', 
                          marginTop: '4px' 
                        }}>
                          当前享受 {((1 - memberInfo.current_discount_rate) * 100).toFixed(1)}% 折扣
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* 快速操作 */}
            <Card 
              title={
                <Space>
                  <CrownOutlined style={{ color: '#1890ff' }} />
                  <span>快速操作</span>
                </Space>
              }
              size="small" 
              style={{ 
                marginTop: 16,
                background: 'linear-gradient(135deg, #f6f8fa 0%, #ffffff 100%)',
                border: '1px solid #e8e8e8'
              }}
            >
              <Alert 
                message="每处理一张化验单，消耗1积分，积分不足时，无法处理化验单。" 
                type="info" 
                style={{ marginBottom: '16px' }}
              />
              
              <Space size="middle">
                <Button 
                  type="primary" 
                  icon={<AlipayOutlined />} 
                  onClick={showRechargeModal}
                  size="large"
                >
                  在线充值
                </Button>
                <Button 
                  icon={<CrownOutlined />} 
                  onClick={showWechatQR}
                  size="large"
                >
                  联系客服
                </Button>
              </Space>
            </Card>

          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text>加载中...</Text>
          </div>
        )}
      </Modal>

      {/* 支付宝充值弹窗 */}
      <Modal
        title={
          <Space>
            <AlipayOutlined style={{ color: '#1890ff' }} />
            <span>支付宝在线充值</span>
          </Space>
        }
        open={rechargeModalVisible}
        onCancel={handleRechargeModalClose}
        footer={[
          <Button key="cancel" onClick={handleRechargeModalClose}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleSubmitPayment}
            loading={processingPayment}
            icon={<AlipayOutlined />}
          >
            {processingPayment ? '处理中...' : '去支付'}
          </Button>
        ]}
        width={600}
        centered
      >
        <div style={{ padding: '20px 0' }}>
          {/* 充值金额输入 */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              充值金额（元）
            </div>
            <InputNumber
              value={rechargeAmount}
              onChange={handleAmountChange}
              min={9.9}
              step={10}
              precision={2}
              style={{ width: '100%' }}
              size="large"
              placeholder="请输入充值金额，最少 9.9 元"
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
              💡 单次充值最少 9.9 元
            </div>
          </div>

          {/* 快捷充值金额 */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              快捷金额
            </div>
            <Space wrap>
              {[9.9, 50, 100, 199, 500, 999].map((amount) => (
                <Button 
                  key={amount}
                  onClick={() => handleAmountChange(amount)}
                  type={rechargeAmount === amount ? 'primary' : 'default'}
                >
                  ¥{amount}
                </Button>
              ))}
            </Space>
          </div>

          {/* 充值预览 */}
          {loadingPreview ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin tip="计算中..." />
            </div>
          ) : rechargePreview ? (
            <Card 
              style={{ 
                background: '#f6ffed', 
                border: '1px solid #b7eb8f',
                marginBottom: '16px'
              }}
            >
              <div style={{ marginBottom: '16px' }}>
                <Title level={5} style={{ margin: 0, color: '#52c41a' }}>
                  充值预览
                </Title>
              </div>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="充值金额"
                    value={rechargePreview.amount}
                    prefix="¥"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="获得积分"
                    value={rechargePreview.total_points}
                    suffix="分"
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              </Row>

              {rechargePreview.bonus_points > 0 && (
                <Alert
                  message={`包含奖励积分 ${rechargePreview.bonus_points} 分`}
                  type="success"
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              )}

              {rechargePreview.level_upgraded && (
                <Alert
                  message={
                    <span>
                      会员等级升级：
                      <Tag color="default" style={{ marginLeft: '8px' }}>
                        {rechargePreview.old_level_name}
                      </Tag>
                      <span style={{ margin: '0 8px' }}>→</span>
                      <Tag color="success">
                        {rechargePreview.new_level_name}
                      </Tag>
                    </span>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: '8px' }}
                />
              )}

              {/* 计算详情 */}
              {rechargePreview.calculation_details && rechargePreview.calculation_details.length > 1 && (
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    💡 充值积分分段计算：
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    {rechargePreview.calculation_details.map((detail, index) => (
                      <div 
                        key={index} 
                        style={{ 
                          fontSize: '12px', 
                          color: '#666',
                          marginTop: '4px'
                        }}
                      >
                        • ¥{detail.amount.toFixed(2)} × {(detail.discount_rate * 100).toFixed(0)}折 = {detail.points} 积分
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ) : null}

          {/* 支付说明 */}
          <Alert
            message="支付说明"
            description={
              <div>
                <div style={{ marginBottom: '4px' }}>
                  1. 点击"去支付"后将打开支付宝支付页面
                </div>
                <div style={{ marginBottom: '4px' }}>
                  2. 请在新窗口完成支付，支付完成后系统自动充值积分
                </div>
                <div>
                  3. 如遇到问题，请联系客服处理
                </div>
              </div>
            }
            type="info"
            showIcon
          />
        </div>
      </Modal>

      {/* 微信二维码弹窗 */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <Space>
              <span style={{ fontSize: '18px' }}>📱</span>
              <span>联系官方微信</span>
            </Space>
          </div>
        }
        open={wechatModalVisible}
        onCancel={handleWechatModalClose}
        footer={null}
        width={400}
        centered
        styles={{
          body: { padding: '24px', textAlign: 'center' }
        }}
      >
        <div style={{ padding: '20px 0' }}>
          {/* 微信二维码 */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '20px' 
          }}>
            <img 
              src="/wechat-qrcode.png" 
              alt="微信二维码" 
              style={{ 
                width: '200px', 
                height: '200px',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                padding: '10px',
                background: '#fff'
              }}
              onError={(e) => {
                // 如果图片加载失败，显示占位符
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            {/* 占位符，当二维码图片加载失败时显示 */}
            <div 
              style={{ 
                width: '200px', 
                height: '200px',
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: '#999',
                background: '#fafafa'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>📱</div>
              <div>微信二维码</div>
            </div>
          </div>

          {/* 说明文字 */}
          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ margin: '0 0 8px 0', color: '#1890ff' }}>
              扫码添加官方微信
            </Title>
            <Text style={{ color: '#666', fontSize: '14px' }}>
              专业客服为您提供：
            </Text>
          </div>

          {/* 服务列表 */}
          <div style={{ textAlign: 'left', margin: '0 auto', maxWidth: '280px' }}>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              ✅ 会员充值服务
            </div>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              ✅ 技术支持与咨询
            </div>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              ✅ 企业合作洽谈
            </div>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              ✅ 定制化解决方案
            </div>
          </div>

          {/* 提示信息 */}
          <div style={{ 
            marginTop: '20px', 
            padding: '12px', 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f',
            borderRadius: '6px'
          }}>
            <Text style={{ fontSize: '12px', color: '#52c41a' }}>
              💡 工作时间：周一至周五 9:00-18:00
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MemberInfo; 